import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

// Shows the row of a source, by the number the source has in the list
type SourceJump = (sourceNumber: number) => void;

interface SourceJumpRegistry {
  // Leads to the row of a source; does nothing while no table is shown
  jump: SourceJump;
  // Called by the source table: names the function that shows a row.
  // Returns the function that withdraws it.
  register: (handler: SourceJump) => () => void;
}

const SourceJumpContext = createContext<SourceJumpRegistry | null>(null);

/**
 * Joins the source marks of a view to its source table.
 *
 * A view wraps its body in it. The source table beneath registers how a row
 * is shown; a source mark beneath asks for the row of its number. Neither
 * knows the other.
 */
export const SourceJumpProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const handler = useRef<SourceJump | null>(null);

  const registry = useMemo<SourceJumpRegistry>(
    () => ({
      jump: (sourceNumber) => handler.current?.(sourceNumber),
      register: (next) => {
        handler.current = next;
        return () => {
          if (handler.current === next) {
            handler.current = null;
          }
        };
      },
    }),
    [],
  );

  return (
    <SourceJumpContext.Provider value={registry}>
      {children}
    </SourceJumpContext.Provider>
  );
};

/**
 * For a source mark: the function that leads to the row of a source.
 * Null outside a provider, where a mark has no table to lead to.
 */
export const useSourceJump = (): SourceJump | null => {
  const registry = useContext(SourceJumpContext);
  return registry ? registry.jump : null;
};

/**
 * For the source table: registers how it shows the row of a source.
 */
export const useSourceJumpTarget = (showRow: SourceJump): void => {
  const registry = useContext(SourceJumpContext);
  const latest = useRef(showRow);
  latest.current = showRow;

  const stable = useCallback<SourceJump>(
    (sourceNumber) => latest.current(sourceNumber),
    [],
  );

  useEffect(
    () => (registry ? registry.register(stable) : undefined),
    [registry, stable],
  );
};
